import React, { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../store';
import { showFeedbackModal } from '../store/actions/UserFeedbackActions';
import Spinner from '../components/shared/TailwindComponents/Spinner';
import MarketplaceGate from '../components/Marketplace/MarketplaceGate';
import {
  SIGNING_LEAD_KEY,
  SIGNING_STATE_PREFIX,
  completeBankIdVerification,
  completeLetterSigning,
  errorMessage,
} from '../services/api/marketplaceService';

const takeSigningLead = (): string | null => {
  try {
    const lead = sessionStorage.getItem(SIGNING_LEAD_KEY);
    sessionStorage.removeItem(SIGNING_LEAD_KEY);
    return lead;
  } catch {
    return null;
  }
};

/**
 * Where the identity provider (BankID, Spanish eID) sends people back: a
 * lawyer from their identity check (back to the inbox), or a client from
 * signing their engagement letter (state starts with `sign.`; back to their
 * request). Hands the code + state to the backend once (an authorization code
 * is single-use, so a re-render must not send it twice).
 */
const CallbackContent: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const sent = useRef(false);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const toast = (modalType: 'success' | 'error' | 'warning', message: string) =>
      dispatch(showFeedbackModal({ showModal: true, modalType, duration: 5000, message }));
    const done = () => history.replace('/marketplace/inbox');
    const signingLead = takeSigningLead();

    if (state?.startsWith(SIGNING_STATE_PREFIX) || (signingLead && !state)) {
      const back = (lead: string | null) => history.replace(lead ? `/marketplace/requests/${lead}` : '/marketplace');
      if (params.get('error') || !code || !state) {
        toast('warning', t('Signing was cancelled. You can sign the letter from your request.'));
        back(signingLead);
        return;
      }
      completeLetterSigning(code, state)
        .then((r) => { toast('success', t('Engagement letter signed')); back(r.lead_uuid); })
        .catch((err) => { toast('error', errorMessage(err, t('BankID signing failed. Try again.'))); back(signingLead); });
      return;
    }

    if (params.get('error') || !code || !state) {
      toast('warning', t('The identity check was cancelled. You can try again from your inbox.'));
      done();
      return;
    }
    completeBankIdVerification(code, state)
      .then(() => toast('success', t('Your identity is confirmed')))
      .catch((err) => toast('error', errorMessage(err, t('Identity verification failed. Try again.'))))
      .finally(done);
  }, [dispatch, history, location.search, t]);

  return (
    <div className="flex h-full items-center justify-center" data-testid="marketplace-verification-callback">
      <Spinner size="md" label={t('Confirming with BankID…')} centered />
    </div>
  );
};

const MarketplaceVerificationCallbackPage: React.FC = () => (
  <MarketplaceGate>{() => <CallbackContent />}</MarketplaceGate>
);

export default MarketplaceVerificationCallbackPage;
